import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, Users, Building2, Link2, CheckCircle2, 
  Clock, Shield, ChevronRight, Plus, Lock, Unlock, 
  GripVertical, RotateCcw, KeyRound, FileText, Upload
} from 'lucide-react';
import AddClientModal from '@/components/modals/AddClientModal';
import AccessRequestModal from '@/components/modals/AccessRequestModal';
import { BulkClientsDialog } from '@/components/bulk/BulkRecordsDialogs';
import { toast } from 'sonner';

export default function ClientsSection({ onImport, onClientIntake }) {
  const navigate = useNavigate();
  const { user, token, organisation, isOrgAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showBulkClientsModal, setShowBulkClientsModal] = useState(false);
  const [accessRequestClient, setAccessRequestClient] = useState(null);
  const [clientAccessStatus, setClientAccessStatus] = useState({});
  
  // Drag and drop state
  const [clientOrder, setClientOrder] = useState([]);
  const [isOrderLocked, setIsOrderLocked] = useState(true);
  const [draggedClient, setDraggedClient] = useState(null);
  const [dragOverClient, setDragOverClient] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      if (!token) return;
      try {
        const response = await api.getClients(token);
        const fetchedClients = response.data;
        
        // Sort alphabetically by default
        const alphabeticalOrder = [...fetchedClients].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        // Check for saved order in localStorage
        if (user?.id) {
          const savedOrder = localStorage.getItem(`qindex_client_order_${user.id}`);
          if (savedOrder) {
            try {
              const parsedOrder = JSON.parse(savedOrder);
              const orderedClients = [];
              parsedOrder.forEach(clientId => {
                const client = alphabeticalOrder.find(c => c.id === clientId);
                if (client) orderedClients.push(client);
              });
              alphabeticalOrder.forEach(client => {
                if (!parsedOrder.includes(client.id)) {
                  orderedClients.push(client);
                }
              });
              setClients(orderedClients);
              setClientOrder(orderedClients.map(c => c.id));
            } catch (e) {
              setClients(alphabeticalOrder);
              setClientOrder(alphabeticalOrder.map(c => c.id));
            }
          } else {
            setClients(alphabeticalOrder);
            setClientOrder(alphabeticalOrder.map(c => c.id));
          }
        } else {
          setClients(alphabeticalOrder);
          setClientOrder(alphabeticalOrder.map(c => c.id));
        }
      } catch (error) {
        console.error('Failed to fetch clients:', error);
      }
      setLoading(false);
    };
    fetchClients();
  }, [token, user?.id]);

  const refreshClients = async () => {
    if (!token) return;
    try {
      const response = await api.getClients(token);
      if (clientOrder.length > 0) {
        const orderedClients = [];
        clientOrder.forEach(clientId => {
          const client = response.data.find(c => c.id === clientId);
          if (client) orderedClients.push(client);
        });
        response.data.forEach(client => {
          if (!clientOrder.includes(client.id)) {
            orderedClients.push(client);
          }
        });
        setClients(orderedClients);
      } else {
        const sorted = [...response.data].sort((a, b) => a.name.localeCompare(b.name));
        setClients(sorted);
        setClientOrder(sorted.map(c => c.id));
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const saveClientOrder = useCallback((order) => {
    if (user?.id) {
      localStorage.setItem(`qindex_client_order_${user.id}`, JSON.stringify(order));
    }
  }, [user?.id]);

  // Check access status for restricted clients
  useEffect(() => {
    const checkRestrictedAccess = async () => {
      if (!token || isOrgAdmin) return;
      
      const restrictedClients = clients.filter(c => c.restricted);
      const accessStatuses = {};
      
      for (const client of restrictedClients) {
        try {
          const response = await api.checkClientAccess(token, client.id);
          accessStatuses[client.id] = response.data;
        } catch (error) {
          accessStatuses[client.id] = { hasAccess: false, reason: 'error' };
        }
      }
      
      setClientAccessStatus(accessStatuses);
    };
    
    if (clients.length > 0) {
      checkRestrictedAccess();
    }
  }, [token, clients, isOrgAdmin]);

  const handleClientClick = (client) => {
    if (!isOrderLocked) return;
    
    // Non-admins need access check for restricted clients
    if (client.restricted && !isOrgAdmin) {
      const accessStatus = clientAccessStatus[client.id];
      
      if (accessStatus?.hasAccess) {
        navigate(`/app/clients/${client.id}`);
      } else if (accessStatus?.reason === 'pending_request') {
        toast.info('Your access request is pending review');
      } else {
        setAccessRequestClient(client);
      }
    } else {
      navigate(`/app/clients/${client.id}`);
    }
  };

  // Drag handlers
  const handleDragStart = (e, clientId) => {
    if (isOrderLocked) return;
    setDraggedClient(clientId);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedClient(null);
    setDragOverClient(null);
  };

  const handleDragOver = (e, clientId) => {
    e.preventDefault();
    if (isOrderLocked || draggedClient === null) return;
    setDragOverClient(clientId);
  };

  const handleDrop = (e, dropClientId) => {
    e.preventDefault();
    if (isOrderLocked || draggedClient === null || draggedClient === dropClientId) return;

    const dragIndex = clients.findIndex(c => c.id === draggedClient);
    const dropIndex = clients.findIndex(c => c.id === dropClientId);
    
    if (dragIndex === -1 || dropIndex === -1) return;

    const newClients = [...clients];
    const [draggedItem] = newClients.splice(dragIndex, 1);
    newClients.splice(dropIndex, 0, draggedItem);
    
    setClients(newClients);
    const newOrder = newClients.map(c => c.id);
    setClientOrder(newOrder);
    saveClientOrder(newOrder);
    
    setDraggedClient(null);
    setDragOverClient(null);
  };

  const handleDragLeave = () => {
    setDragOverClient(null);
  };

  const toggleOrderLock = () => {
    setIsOrderLocked(!isOrderLocked);
    if (!isOrderLocked) {
      toast.success('Client order saved');
    }
  };

  const resetClientOrder = () => {
    const sorted = [...clients].sort((a, b) => a.name.localeCompare(b.name));
    setClients(sorted);
    const newOrder = sorted.map(c => c.id);
    setClientOrder(newOrder);
    saveClientOrder(newOrder);
    toast.success('Reset to alphabetical order');
  };

  const handleAddClient = async (clientData) => {
    try {
      const response = await api.createClient(token, clientData);
      toast.success('Client created successfully');
      refreshClients();
      setShowAddClientModal(false);
    } catch (error) {
      toast.error('Failed to create client');
    }
  };

  // Filter clients
  const filteredClients = clients.filter(client => {
    const matchesSearch = !searchQuery || 
      client.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSegment = segmentFilter === 'all' || client.segment === segmentFilter;
    return matchesSearch && matchesSegment;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5D524D]">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="clients-section">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading text-[#2C2420]">Family Groups</h2>
          <p className="text-sm text-[#5D524D]">
            {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''} in your portfolio
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {!isOrderLocked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetClientOrder}
              className="text-xs text-[#8C8078] hover:text-[#4A3B32]"
              data-testid="reset-client-order-btn"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Reset A-Z
            </Button>
          )}
          <Button
            variant={isOrderLocked ? "ghost" : "secondary"}
            size="sm"
            onClick={toggleOrderLock}
            className={cn(
              "text-xs",
              isOrderLocked 
                ? "text-[#8C8078] hover:text-[#4A3B32]" 
                : "bg-[#4A3B32]/10 text-[#4A3B32]"
            )}
            data-testid="client-order-lock-btn"
          >
            {isOrderLocked ? (
              <>
                <Lock className="w-3 h-3 mr-1" />
                Reorder
              </>
            ) : (
              <>
                <Unlock className="w-3 h-3 mr-1" />
                Save Order
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Reorder hint */}
      {!isOrderLocked && (
        <div className="mb-4 p-3 bg-[#4A3B32]/5 border border-[#4A3B32]/10 rounded-lg">
          <p className="text-sm text-[#5D524D]">
            <GripVertical className="w-4 h-4 inline mr-1" />
            Drag and drop client cards to reorder. Click "Save Order" when done.
          </p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clients..."
            className="pl-9"
          />
        </div>
        
        <Select value={segmentFilter} onValueChange={setSegmentFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Segments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Segments</SelectItem>
            <SelectItem value="Family Office">Family Office</SelectItem>
            <SelectItem value="Advice">Advice</SelectItem>
          </SelectContent>
        </Select>
        
        {onClientIntake && (
          <Button 
            variant="outline"
            onClick={onClientIntake}
            className="border-[#4A3B32] text-[#4A3B32] hover:bg-[#4A3B32]/10"
            data-testid="client-intake-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Intake Wizard
          </Button>
        )}

        <Button 
          onClick={() => setShowBulkClientsModal(true)}
          variant="outline"
          data-testid="bulk-clients-btn"
          title="Upload a CSV to create multiple clients at once"
        >
          <Upload className="w-4 h-4 mr-2" />
          Bulk Import
        </Button>

        <Button 
          onClick={() => setShowAddClientModal(true)}
          className="bg-[#4A3B32] hover:bg-[#3A2E26]"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Client Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClients.map((client, index) => {
          const accessStatus = clientAccessStatus[client.id];
          const needsAccessRequest = client.restricted && !isOrgAdmin && !accessStatus?.hasAccess;
          const hasPendingRequest = accessStatus?.reason === 'pending_request';
          
          return (
            <div
              key={client.id}
              draggable={!isOrderLocked && !searchQuery && segmentFilter === 'all'}
              onDragStart={(e) => handleDragStart(e, client.id)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, client.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, client.id)}
              className={cn(
                "bg-white rounded-xl border p-5 cursor-pointer relative",
                "hover:shadow-md transition-all",
                needsAccessRequest && !hasPendingRequest 
                  ? "border-amber-200 hover:border-amber-300" 
                  : hasPendingRequest
                    ? "border-blue-200 hover:border-blue-300"
                    : "border-[#E6E0D9] hover:border-[#4A3B32]/30",
                !isOrderLocked && !searchQuery && segmentFilter === 'all' && "cursor-grab active:cursor-grabbing",
                draggedClient === client.id && "opacity-50",
                dragOverClient === client.id && draggedClient !== client.id && "ring-2 ring-[#4A3B32] ring-opacity-50"
              )}
              onClick={() => handleClientClick(client)}
              data-testid={`client-card-${client.id}`}
            >
              {/* Drag Handle */}
              {!isOrderLocked && !searchQuery && segmentFilter === 'all' && (
                <div className="absolute top-2 right-2 p-1 text-[#8C8078] opacity-50 hover:opacity-100">
                  <GripVertical className="w-4 h-4" />
                </div>
              )}

              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading text-lg text-[#2C2420]">{client.name}</h3>
                    {client.restricted && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                        <Shield className="w-3 h-3" />
                        Restricted
                      </span>
                    )}
                  </div>
                  <span className={cn(
                    "text-xs px-2 py-0.5 rounded-full",
                    client.segment === 'Family Office' 
                      ? 'bg-[#4A3B32]/10 text-[#4A3B32]' 
                      : 'bg-[#C6A88F]/20 text-[#5D524D]'
                  )}>
                    {client.segment}
                  </span>
                </div>
                {needsAccessRequest ? (
                  hasPendingRequest ? (
                    <div className="flex items-center gap-1 text-blue-600">
                      <Clock className="w-4 h-4" />
                      <span className="text-xs font-medium">Pending</span>
                    </div>
                  ) : (
                    <KeyRound className="w-5 h-5 text-amber-500" />
                  )
                ) : (
                  <ChevronRight className="w-5 h-5 text-[#8C8078]" />
                )}
              </div>

              {/* Access Request Banner */}
              {needsAccessRequest && (
                <div className={cn(
                  "mb-4 p-3 rounded-lg text-sm",
                  hasPendingRequest 
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                )}>
                  {hasPendingRequest ? (
                    <p className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Access request pending approval
                    </p>
                  ) : (
                    <p className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Click to request access
                    </p>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#8C8078]" />
                  <span className="text-sm text-[#5D524D]">
                    {client.stats?.entities || 0} Entities
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#8C8078]" />
                  <span className="text-sm text-[#5D524D]">
                    {client.stats?.individuals || 0} Individuals
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-[#8C8078]" />
                  <span className="text-sm text-[#5D524D]">
                    {client.stats?.linksVerified || 0} Links
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#8C8078]" />
                  <span className="text-sm text-[#5D524D]">
                    {client.stats?.tasksDue || 0} Tasks Due
                  </span>
                </div>
              </div>

              {/* Net Worth */}
              {client.netWorth && !needsAccessRequest && (
                <div className="mt-4 pt-4 border-t border-[#E6E0D9]">
                  <p className="text-xs text-[#8C8078]">Net Worth</p>
                  <p className="text-lg font-heading text-[#2C2420]">
                    {formatCurrency(client.netWorth)}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredClients.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-[#8C8078] mx-auto mb-4" />
          <h3 className="text-lg font-heading text-[#2C2420] mb-2">No clients found</h3>
          <p className="text-sm text-[#5D524D] mb-4">
            {searchQuery ? 'Try adjusting your search' : 'Get started by adding your first client'}
          </p>
          {!searchQuery && (
            <Button 
              onClick={() => setShowAddClientModal(true)}
              className="bg-[#4A3B32] hover:bg-[#3A2E26]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          )}
        </div>
      )}

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onSave={handleAddClient}
      />

      {/* Bulk Clients Import */}
      <BulkClientsDialog
        open={showBulkClientsModal}
        onOpenChange={setShowBulkClientsModal}
        onImported={async () => {
          try {
            const response = await api.getClients(token);
            const alphabetical = [...response.data].sort((a, b) => a.name.localeCompare(b.name));
            setClients(alphabetical);
          } catch (e) { /* surfaced via toast already */ }
        }}
      />

      {/* Access Request Modal */}
      <AccessRequestModal
        isOpen={!!accessRequestClient}
        onClose={() => setAccessRequestClient(null)}
        client={accessRequestClient}
        onSuccess={() => {
          // Refresh access status
          if (accessRequestClient) {
            setClientAccessStatus(prev => ({
              ...prev,
              [accessRequestClient.id]: { hasAccess: false, reason: 'pending_request' }
            }));
          }
        }}
      />
    </div>
  );
}
